/**
 * EFFET : enregistre en continu les dernières secondes du joueur local, pour
 * le ralenti. Vidéo BRUTE (jamais filtrée) — ou, si le joueur a choisi
 * « seulement ton maillage », son maillage : sa vidéo ne sort pas.
 * Rien n'est stocké ailleurs qu'en mémoire.
 *
 * Capture en images brutes (synchrone, ~1 ms), compression JPEG seulement au
 * verdict : sur une machine chargée, un encodage JPEG prend jusqu'à 1,5 s, et
 * encoder en continu faisait tomber la capture à ~1 img/s.
 */
import type { FaceSource } from '../detection/faceSource';
import { createThrottle } from '../lib/throttle';
import { encodeMeshFrame } from '../mesh/codec';
import { createClipBuffer } from './clipBuffer';
import type { Clip, ClipKind } from './clipCodec';
import { encodeFrames } from './encodeFrames';

const WINDOW_MS = 4000;
const VIDEO_FPS = 8;
const MESH_FPS = 12;
const WIDTH = 320;
const HEIGHT = 240;
const JPEG_QUALITY = 0.6;

export interface ReplayRecorder {
  /** Les `lastMs` dernières millisecondes, dans le format choisi par le joueur. */
  clip(lastMs: number): Promise<Clip>;
  stop(): void;
}

/** Une image brute → JPEG (un canvas par image : les encodages tournent en parallèle). */
function toJpeg(image: ImageData): Promise<Uint8Array | null> {
  const c = document.createElement('canvas');
  c.width = image.width;
  c.height = image.height;
  c.getContext('2d')?.putImageData(image, 0, 0);
  return new Promise((resolve) => {
    c.toBlob(
      (blob) => {
        if (!blob) resolve(null);
        else
          void blob.arrayBuffer().then(
            (b) => {
              resolve(new Uint8Array(b));
            },
            () => {
              resolve(null);
            },
          );
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}

export function createReplayRecorder(options: {
  video: HTMLVideoElement;
  faceSource: FaceSource;
  kind: () => ClipKind;
}): ReplayRecorder {
  const video = createClipBuffer<ImageData>(WINDOW_MS);
  const mesh = createClipBuffer<Uint8Array>(WINDOW_MS);
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  const capture = setInterval(() => {
    const el = options.video;
    if (options.kind() !== 'video' || !ctx || el.readyState < 2) return;
    ctx.drawImage(el, 0, 0, WIDTH, HEIGHT);
    video.push(performance.now(), ctx.getImageData(0, 0, WIDTH, HEIGHT));
  }, 1000 / VIDEO_FPS);

  const throttle = createThrottle(MESH_FPS);
  let seq = 0;
  const offFace = options.faceSource.subscribe((frame) => {
    if (options.kind() !== 'mesh' || !throttle(frame.t)) return;
    const bytes = encodeMeshFrame({ seq: ++seq, t: frame.t, points: frame.landmarks ?? [] });
    mesh.push(frame.t, new Uint8Array(bytes));
  });

  return {
    async clip(lastMs) {
      if (options.kind() === 'mesh') return { kind: 'mesh', frames: mesh.snapshot(lastMs) };
      return { kind: 'video', frames: await encodeFrames(video.snapshot(lastMs), toJpeg) };
    },
    stop() {
      clearInterval(capture);
      offFace();
      video.clear();
      mesh.clear();
    },
  };
}
